import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, vModelSelect as _vModelSelect, withDirectives as _withDirectives, vModelText as _vModelText, normalizeClass as _normalizeClass, createBlock as _createBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, createTextVNode as _createTextVNode, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "margin-b-standard metadata-type" }
const _hoisted_2 = ["disabled"]
const _hoisted_3 = ["value"]
const _hoisted_4 = { class: "margin-b-standard metadata-name" }
const _hoisted_5 = {
  key: 0,
  class: "margin-b-standard metadata-name-url"
}
const _hoisted_6 = {
  key: 1,
  class: "margin-b-standard metadata-options"
}
const _hoisted_7 = {
  key: 1,
  class: "pad-0-left"
}
const _hoisted_8 = ["onUpdate:modelValue", "disabled"]
const _hoisted_9 = ["onClick"]
const _hoisted_10 = ["onClick"]
const _hoisted_11 = ["onClick"]
const _hoisted_12 = ["disabled"]
const _hoisted_13 = ["disabled"]

import { ref, computed, onMounted } from "vue";
import { sanitizeFriendlyURL } from "@/utils/utils";
import { v4 as uuidv4 } from "uuid";
import { MetadataAllowedValues } from "@/models/content-templates.model";
import { AxiosResponse } from "axios";
import { DefinitionToCreateResponse } from "@/models/api/metadata.model";
import { mdiDelete, mdiClose, mdiPencil, mdiPlus } from "@mdi/js";
import { useUIContentTemplatesStore } from "@/stores/ui-content-template.store";
import { ContentTemplateForEditingMetadata } from "@/models/store/ui-state/content-template-for-adding-metadata.model";
import { useAssetsMetadataStore } from "@/stores/assets-metadata.store";
import { useMetadataStore } from "@/stores/metadata.store";
import { useRoute } from "vue-router";
import { METADATA } from "@/constants/template.constants";
import ErrorMessage from "@/components/ErrorMessage.vue";
import Popup from "@/components/Popup.vue";

const TEXT_FREE = "Text Free";

interface Option {
  beingDeleted: boolean;
  created_by?: number;
  created_on?: number;
  display_name: string;
  display_order: number;
  editable: boolean;
  id: number | string;
  isNew: boolean;
  metadata_definition_id?: number;
  oldValue?: string;
  updated_by?: number;
  updated_on?: number;
  oldDisplayName: string;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'AddMetadata',
  setup(__props) {

const uiContentTemplatesStore = useUIContentTemplatesStore();
const metadataStore = useMetadataStore();
const assetsMetadataStore = useAssetsMetadataStore();
const route = useRoute();

const isDataSubmitting = ref<boolean>(false);

const name = ref<string>("");
const nameError = ref<string>("");
const removeNameError = (): void => {
  nameError.value = "";
};

const friendlyURL = ref<string>("");
const friendlyNameError = ref<string>("");

const areOptionsVisible = ref<boolean>(false);
const option = ref<string>("");
const optionError = ref<string>("");
const removeOptionError = (): void => {
  optionError.value = "";
};
const optionsError = ref<string>("");
const removeOptionsError = (): void => {
  optionsError.value = "";
};
const options = ref<Option[]>([]);
const toggleOptions = (): void => {
  type.value !== TEXT_FREE
    ? (areOptionsVisible.value = true)
    : (areOptionsVisible.value = false);
};
const setOptions = (): void => {
  const template =
    uiContentTemplatesStore.contentTemplateForAddingMetadata as ContentTemplateForEditingMetadata;
  if (template.allowedValues && template.allowedValues.length > 0) {
    options.value = template.allowedValues.map(
      (item: MetadataAllowedValues) => {
        return {
          ...item,
          isNew: false,
          editable: false,
          beingDeleted: false,
          oldDisplayName: item.display_name
        };
      }
    );
  }
};
const getBeingDeletedOptions = (): Option[] => {
  return options.value.filter((option: Option): boolean => {
    return option.beingDeleted;
  });
};
const getEditableOptions = (): Option[] => {
  return options.value.filter((option: Option): boolean => {
    return option.editable;
  });
};
const isOptionExisting = (): boolean => {
  if (options.value.length > 0) {
    return options.value.some((opt: Option): boolean => {
      return opt.display_name === option.value;
    });
  }
  return false;
};
const checkDuplicateOptions = (): boolean => {
  const allNames = options.value
    .filter((opt: Option): boolean => {
      return !opt.beingDeleted;
    })
    .map((option: Option): string => {
      return option.display_name.toLowerCase();
    });
  const hasDuplicates = allNames.some((item: string, index: number) => {
    return allNames.lastIndexOf(item) !== index;
  });

  if (hasDuplicates) {
    optionsError.value = "You have duplicate options";
  } else {
    removeOptionsError();
  }
  return hasDuplicates;
};
const checkOptionsCount = (): boolean => {
  const isEnoughOptions =
    type.value !== TEXT_FREE &&
    options.value.length - getBeingDeletedOptions().length < 2;
  if (isEnoughOptions) {
    optionsError.value = "Please add 2 or more options";
  }
  return isEnoughOptions;
};
const startEditing = (option: Option): void => {
  option.editable = true;
};
const cancelEditing = (option: Option): void => {
  option.editable = false;
  option.display_name = option.oldDisplayName;
  removeOptionsError();
};
const createOption = (): void => {
  if (!option.value) {
    optionError.value = "Option cannot be empty";
    return;
  }
  if (isOptionExisting()) {
    optionError.value = "This option already exists";
    return;
  }
  optionsError.value = "";

  options.value.push({
    beingDeleted: false,
    display_name: option.value,
    display_order: options.value.length + 1,
    editable: false,
    id: uuidv4(),
    isNew: true,
    oldDisplayName: option.value
  });
  option.value = "";
};
const getNewOptions = (): Option[] => {
  return options.value.filter((option: Option): boolean => {
    return option.isNew;
  });
};
const createOptions = (): void => {
  const template = uiContentTemplatesStore.contentTemplateForAddingMetadata;
  if (template) {
    const valuesArray = getNewOptions().map((option: Option) => {
      const metadataDefinitionId = newDefinitionId.value
        ? newDefinitionId.value
        : (template as ContentTemplateForEditingMetadata).id;
      return {
        display_name: option.display_name,
        metadata_definition_id: metadataDefinitionId,
        display_order: option.display_order
      };
    });

    const payload = {
      values: valuesArray,
      productId: template.productId
    };
    metadataStore.createMetadataAllowedValues(payload);
  }
};
const removeOption = (option: Option): void => {
  cancelEditing(option);
  option.beingDeleted = true;
  if (option.isNew) {
    options.value = options.value.filter((opt: Option): boolean => {
      return opt.id !== option.id;
    });
  }
};

const type = ref<string>(TEXT_FREE);

const newDefinitionId = ref<number | undefined>(undefined);

const validateInput = (): boolean => {
  let isValid = true;
  if (!name.value) {
    nameError.value = "Name cannot be empty";
    isValid = false;
  }
  if (friendlyURL.value === "") {
    friendlyURL.value = name.value;
  }
  if (
    friendlyURL.value &&
    !METADATA.FRIENDLY_URL_PATTERN.test(friendlyURL.value)
  ) {
    friendlyURL.value = sanitizeFriendlyURL(friendlyURL.value);
  }
  return isValid;
};
const isValid = (): boolean => {
  return checkDuplicateOptions() || !validateInput() || checkOptionsCount();
};

const createMetadata = (): void => {
  const template = uiContentTemplatesStore.contentTemplateForAddingMetadata;
  if (template) {
    if (isValid()) {
      return;
    }

    isDataSubmitting.value = true;
    const payload = {
      metadata: {
        section_type_id: template.sectionTypeId,
        name: name.value,
        type: type.value,
        url_name: friendlyURL.value
      },
      productId: template.productId
    };
    metadataStore
      .createMetadataDefinition(payload)
      .then((response: AxiosResponse<DefinitionToCreateResponse>) => {
        newDefinitionId.value = response.data.data.id;
        if (type.value !== TEXT_FREE) {
          createOptions();
        }
        if (route.name === "assets") {
          assetsMetadataStore.isAssetsMetadataUpdating = true;
          assetsMetadataStore.populateAssetsMetadata(template.sectionTypeId);
        }
        isDataSubmitting.value = false;
        uiContentTemplatesStore.closeAddMetadataModal();
      });
  }
};
const editMetadata = async (): Promise<unknown> => {
  const template =
    uiContentTemplatesStore.contentTemplateForAddingMetadata as ContentTemplateForEditingMetadata;
  if (template) {
    if (isValid()) {
      return;
    }

    isDataSubmitting.value = true;
    if (
      name.value !== template.name ||
      friendlyURL.value !== template.friendlyURL
    ) {
      const payload = {
        metadata: {
          id: template.id,
          name: name.value,
          url_name: friendlyURL.value
        },
        productId: template.productId
      };
      await metadataStore.updateMetadataDefinition(payload);
    }
    if (type.value !== TEXT_FREE && getNewOptions().length > 0) {
      createOptions();
    }
    if (type.value !== TEXT_FREE && getEditableOptions().length > 0) {
      await Promise.all(
        getEditableOptions()
          .filter((option: Option): boolean => {
            return !option.isNew;
          })
          .map((option: Option): Promise<void> | void => {
            if (option.id) {
              const payload = {
                value: {
                  id: Number(option.id),
                  display_name: option.display_name,
                  display_order: option.display_order
                },
                productId: template.productId
              };
              return metadataStore.updateMetadataAllowedValues(payload);
            }
          })
      );
    }
    if (type.value !== TEXT_FREE && getBeingDeletedOptions().length > 0) {
      await Promise.all(
        getBeingDeletedOptions().map((option: Option): Promise<void> | void => {
          if (option.id) {
            const payload = {
              id: Number(option.id),
              productId: template.productId
            };
            return metadataStore.deleteMetadataAllowedValues(payload);
          }
        })
      );
    }
    if (route.name === "assets") {
      assetsMetadataStore.isAssetsMetadataUpdating = true;
      await assetsMetadataStore.populateAssetsMetadata(template.sectionTypeId);
    }
    isDataSubmitting.value = false;
    uiContentTemplatesStore.closeAddMetadataModal();
  }
};

const modalTitle = computed<string>(() => {
  return uiContentTemplatesStore.contentTemplateForAddingMetadata &&
    uiContentTemplatesStore.contentTemplateForAddingMetadata.modalType ===
      "edit"
    ? "Edit Metadata"
    : "Add Metadata";
});
onMounted(() => {
  const template = uiContentTemplatesStore.contentTemplateForAddingMetadata;
  if (template && template.modalType === "edit") {
    name.value = (template as ContentTemplateForEditingMetadata).name ?? "";
    friendlyURL.value =
      (template as ContentTemplateForEditingMetadata).friendlyURL ?? "";
    type.value =
      (template as ContentTemplateForEditingMetadata).type ?? TEXT_FREE;
    areOptionsVisible.value = type.value !== TEXT_FREE;
    setOptions();
  }
});

return (_ctx: any,_cache: any) => {
  const _component_SVGIcon = _resolveComponent("SVGIcon")!

  return (_openBlock(), _createBlock(Popup, {
    onClosePopup: _unref(uiContentTemplatesStore).closeAddMetadataModal
  }, {
    header: _withCtx(() => [
      _createElementVNode("h3", null, _toDisplayString(modalTitle.value), 1)
    ]),
    body: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _cache[11] || (_cache[11] = _createElementVNode("p", { class: "pad-0-bottom" }, "Type:", -1)),
        _withDirectives(_createElementVNode("select", {
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((type).value = $event)),
          disabled: 
            _unref(uiContentTemplatesStore).contentTemplateForAddingMetadata.type
          ,
          onChange: _cache[1] || (_cache[1] = ($event: any) => (toggleOptions()))
        }, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(METADATA).TYPES, (type) => {
            return (_openBlock(), _createElementBlock("option", {
              key: type.id,
              value: type.name
            }, _toDisplayString(type.title), 9, _hoisted_3))
          }), 128))
        ], 40, _hoisted_2), [
          [_vModelSelect, type.value]
        ])
      ]),
      _createElementVNode("div", _hoisted_4, [
        _cache[12] || (_cache[12] = _createElementVNode("p", { class: "pad-0-bottom" }, "Name:", -1)),
        _withDirectives(_createElementVNode("input", {
          "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((name).value = $event)),
          type: "text",
          class: _normalizeClass({ warning: nameError.value }),
          onInput: _cache[3] || (_cache[3] = ($event: any) => (removeNameError()))
        }, null, 34), [
          [
            _vModelText,
            name.value,
            void 0,
            { trim: true }
          ]
        ]),
        (nameError.value)
          ? (_openBlock(), _createBlock(ErrorMessage, {
              key: 0,
              message: nameError.value
            }, null, 8, ["message"]))
          : _createCommentVNode("", true)
      ]),
      (_unref(uiContentTemplatesStore).contentTemplateForAddingMetadata.isXapi)
        ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
            _cache[13] || (_cache[13] = _createElementVNode("p", { class: "pad-0-bottom" }, "URL Friendly Name:", -1)),
            _withDirectives(_createElementVNode("input", {
              "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((friendlyURL).value = $event)),
              type: "text",
              class: _normalizeClass({ warning: friendlyNameError.value })
            }, null, 2), [
              [_vModelText, friendlyURL.value]
            ]),
            (friendlyNameError.value)
              ? (_openBlock(), _createBlock(ErrorMessage, {
                  key: 0,
                  message: friendlyNameError.value
                }, null, 8, ["message"]))
              : _createCommentVNode("", true)
          ]))
        : _createCommentVNode("", true),
      (areOptionsVisible.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
            _cache[15] || (_cache[15] = _createElementVNode("p", { class: "pad-0-bottom" }, "Option:", -1)),
            _withDirectives(_createElementVNode("input", {
              "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((option).value = $event)),
              type: "text",
              class: _normalizeClass({ warning: optionError.value }),
              onInput: _cache[6] || (_cache[6] = ($event: any) => (removeOptionError()))
            }, null, 34), [
              [_vModelText, option.value]
            ]),
            (optionError.value)
              ? (_openBlock(), _createBlock(ErrorMessage, {
                  key: 0,
                  message: optionError.value
                }, null, 8, ["message"]))
              : _createCommentVNode("", true),
            _createElementVNode("button", {
              class: "btn-3 margin-b-standard",
              onClick: _cache[7] || (_cache[7] = ($event: any) => (createOption()))
            }, [
              _createVNode(_component_SVGIcon, { path: _unref(mdiPlus) }, null, 8, ["path"]),
              _cache[14] || (_cache[14] = _createTextVNode(" Add option "))
            ]),
            (options.value.length > 0)
              ? (_openBlock(), _createElementBlock("ul", _hoisted_7, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(options.value, (option) => {
                    return (_openBlock(), _createElementBlock("li", {
                      key: option.id,
                      class: _normalizeClass(['value', 'margin-b-half', { hidden: option.beingDeleted }]),
                      ref_for: true,
                      ref: "existing-option-item"
                    }, [
                      _withDirectives(_createElementVNode("input", {
                        type: "text",
                        class: "value-input",
                        "onUpdate:modelValue": ($event: any) => ((option.display_name) = $event),
                        disabled: !option.editable,
                        ref_for: true,
                        ref: "existing-option-input",
                        onInput: _cache[8] || (_cache[8] = ($event: any) => (removeOptionsError()))
                      }, null, 40, _hoisted_8), [
                        [_vModelText, option.display_name]
                      ]),
                      (!option.editable)
                        ? (_openBlock(), _createElementBlock("button", {
                            key: 0,
                            class: "btn-3 margin-r-half",
                            ref_for: true,
                            ref: "existing-option-edit-btn",
                            onClick: ($event: any) => (startEditing(option))
                          }, [
                            _createVNode(_component_SVGIcon, { path: _unref(mdiPencil) }, null, 8, ["path"])
                          ], 8, _hoisted_9))
                        : _createCommentVNode("", true),
                      (option.editable)
                        ? (_openBlock(), _createElementBlock("button", {
                            key: 1,
                            class: "btn-3 margin-r-half",
                            ref_for: true,
                            ref: "existing-option-cancel-editing-btn",
                            onClick: ($event: any) => (cancelEditing(option))
                          }, [
                            _createVNode(_component_SVGIcon, { path: _unref(mdiClose) }, null, 8, ["path"])
                          ], 8, _hoisted_10))
                        : _createCommentVNode("", true),
                      _createElementVNode("button", {
                        class: "btn-3",
                        onClick: ($event: any) => (removeOption(option))
                      }, [
                        _createVNode(_component_SVGIcon, { path: _unref(mdiDelete) }, null, 8, ["path"])
                      ], 8, _hoisted_11)
                    ], 2))
                  }), 128))
                ]))
              : _createCommentVNode("", true),
            (optionsError.value)
              ? (_openBlock(), _createBlock(ErrorMessage, {
                  key: 2,
                  message: optionsError.value
                }, null, 8, ["message"]))
              : _createCommentVNode("", true)
          ]))
        : _createCommentVNode("", true)
    ]),
    footer: _withCtx(() => [
      _createElementVNode("button", {
        disabled: isDataSubmitting.value,
        class: "btn-2",
        type: "button",
        onClick: _cache[9] || (_cache[9] = ($event: any) => (_unref(uiContentTemplatesStore).closeAddMetadataModal()))
      }, " Cancel ", 8, _hoisted_12),
      _createElementVNode("button", {
        disabled: isDataSubmitting.value,
        class: "btn-1",
        type: "button",
        onClick: _cache[10] || (_cache[10] = ($event: any) => (
          _unref(uiContentTemplatesStore).contentTemplateForAddingMetadata.modalType ===
          'add'
            ? createMetadata()
            : editMetadata()
        ))
      }, " Save ", 8, _hoisted_13)
    ]),
    _: 1
  }, 8, ["onClosePopup"]))
}
}

})